import React, { useCallback, useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Edit2, Trash2, Plus, AlertCircle } from "react-feather";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  addExtraWorkingDayApi,
  listOfExtraWorkingDayApi,
  editExtraWorkingDayApi,
  deleteExtraWorkingDayApi,
} from "../../../../Api/extraWorkingDayApi";
import BaseModal from "../../../../BaseComponents/BaseModal";
import BaseDatePicker from "../../../../BaseComponents/BaseDatePicker";
import TableContainer from "../../../../BaseComponents/BaseTable";
import { useSelectedCenter } from "../../../CenterContext";
import { useNavigate } from "react-router-dom";
import {
  checkStatusCodeSuccess,
  handleArrayResponse,
  tomorrowDate,
  rendorBackButton,
} from "../../../../Constant/common";
import {
  Action,
  Add,
  Date as DateLabel,
  Delete,
  Edit,
  notFound,
  Search,
  Submit,
  Update,
} from "../../../../Constant";
import { leaveBalanceEnums } from "../../../../Constant/HR/leaveBalance";
import { InputPlaceHolder, validationMessages } from "../../../../Constant/validation";
import { SrNo } from "../../../../Constant/Position/position";
import { extraWorkingDayTitle } from "../../../../Constant/title";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";

const ExtraWorkingDay = () => {
  const [modal, setModal] = useState(false);
  const [addEditModal, setAddEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [list, setList] = useState([]);
  const [searchedList, setSearchedList] = useState([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState();
  const [totalRecords, setTotalRecords] = useState();
  const [totalNumberOfRows, setTotalNumberOfRows] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [idToDelete, setIdToDelete] = useState();
  const [sortOrder, setSortOrder] = useState("desc");
  const [columnName, setColumnName] = useState("id");
  const navigate = useNavigate();
  const { selectedCenterPayload } = useSelectedCenter();
  const selectedCenter = selectedCenterPayload.center_id;
  const { setLoading } = usePageLoader();

  useEffect(() => {
    document.title = extraWorkingDayTitle;
  }, []);

  const fetchList = useCallback(() => {
    setLoading(true);
    const payload = {
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
      search: searchValue,
      center_id: selectedCenter,
    };
    listOfExtraWorkingDayApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setList(resp?.data?.extraWorkingDays || []);
          setSearchedList(resp?.data?.extraWorkingDays);
          setTotalPages(resp?.data?.totalPages);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        } else {
          setSearchedList([]);
        }
      })
      .catch(() => setSearchedList([]))
      .finally(() => setLoading(false));
  }, [columnName, currentPage, customPageSize, searchValue, selectedCenter, sortOrder, setLoading]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
    }
  }, [searchValue]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: editData ? editData.working_date : "",
    },
    validationSchema: yup.object({
      date: yup.string().required(validationMessages.required(DateLabel)),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        working_day: values.date,
        center_id: selectedCenter,
        ...(editId && { working_day_id: editId }),
      };
      const apiCall = editId ? editExtraWorkingDayApi(payload) : addExtraWorkingDayApi(payload);

      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp.message);
            fetchList();
            formik.resetForm();
            setAddEditModal(false);
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  const columns = useMemo(
    () => [
      {
        header: SrNo,
        accessorKey: "id",
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
        enableColumnFilter: false,
      },
      { header: DateLabel, accessorKey: "working_date", enableColumnFilter: false },
      {
        header: leaveBalanceEnums.addedBy,
        accessorKey: "addedByUser.name",
        enableColumnFilter: false,
      },
      {
        header: Action,
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cell) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <BaseTooltip label={Edit} color="#b45309">
              <button
                onClick={() => {
                  setEditId(cell.row.original.id);
                  setEditData(cell.row.original);
                  setAddEditModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  border: "1px solid var(--glass-border)",
                  background: "rgba(245,158,11,0.08)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Edit2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={Delete} color="#be123c">
              <button
                onClick={() => {
                  setIdToDelete(cell.row.original.id);
                  setModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  border: "1px solid var(--glass-border)",
                  background: "rgba(244,63,94,0.08)",
                  color: "#f43f5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
          </div>
        ),
      },
    ],
    [currentPage, customPageSize],
  );

  const handleFetchData = (page) => {
    setCurrentPage(page);
  };

  const handleSearchValueChange = (value) => {
    if (value !== searchValue) {
      unstable_batchedUpdates(() => {
        setSearchValue(value);
        setCurrentPage(1);
      });
    }
  };

  const handleFetchSorting = (page, id, order) => {
    unstable_batchedUpdates(() => {
      setCurrentPage(page);
      setColumnName(id);
      setSortOrder(order);
    });
  };
  const deleteEntry = () => {
    setDeleteLoader(true);
    deleteExtraWorkingDayApi(idToDelete)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp.message);
          fetchList();
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => handleArrayResponse(err))
      .finally(() => {
        setDeleteLoader(false);
        setModal(false);
      });
  };

  const fetchEditData = useCallback(
    (id) => {
      const extraWorkingDay = list?.find((item) => item.id === id);
      setEditData(extraWorkingDay);
      setAddEditModal(true);
    },
    [list],
  );

  useEffect(() => {
    if (editId && !editData) {
      fetchEditData(editId);
    }
  }, [editData, editId, fetchEditData]);

  const handleCloseAddEditModal = () => {
    formik.resetForm();
    setEditId(null);
    setEditData(null);
    setAddEditModal(false);
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {leaveBalanceEnums.extraWorkingDay}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Record and manage additional working days for staff
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => {
              setEditId(null);
              setEditData(null);
              setAddEditModal(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 10,
              background: "var(--brand-indigo)",
              border: "none",
              color: "#fff",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus size={15} strokeWidth={2.5} /> {Add}
          </button>
          {rendorBackButton(() => navigate("/setting"))}
        </div>
      </div>

      <BaseModal
        isOpen={modal}
        title={`${Delete} ${leaveBalanceEnums.extraWorkingDay}`}
        toggler={() => setModal(false)}
        submit={deleteEntry}
        submitText={Delete}
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        {leaveBalanceEnums.extraWorkingDeleteConfirmation}
      </BaseModal>

      <BaseModal
        isOpen={addEditModal}
        title={
          editId
            ? `${Edit} ${leaveBalanceEnums.extraWorkingDay}`
            : `${Add} ${leaveBalanceEnums.extraWorkingDay}`
        }
        toggler={handleCloseAddEditModal}
        submit={formik.handleSubmit}
        submitText={editId ? Update : Submit}
        disabled={btnLoader}
        loader={btnLoader}
        size="sm"
        bodyClass="extra-working-day-modal-body"
        dialogClass="extra-working-day-modal-dialog"
      >
        <form onSubmit={formik.handleSubmit}>
          <div>
            <BaseDatePicker
              name="date"
              label={DateLabel}
              value={formik.values.date}
              onChange={(val) => formik.setFieldValue("date", val)}
              onBlur={formik.handleBlur}
              touched={formik.touched.date}
              error={formik.errors.date}
              placeholder={InputPlaceHolder(DateLabel)}
              minDate={new window.Date(tomorrowDate)}
              required
              popperPlacement="right-start"
            />
          </div>
        </form>
      </BaseModal>

      <div
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {list && list.length > 0 ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            fetchData={handleFetchData}
            columns={columns}
            data={searchValue ? searchedList : list || []}
            isGlobalFilter={true}
            shouldLineBreak={false}
            manualFiltering={true}
            onSearch={handleSearchValueChange}
            customPageSize={customPageSize}
            setCustomPageSize={setCustomPageSize}
            manualPagination={true}
            fetchSortingData={handleFetchSorting}
            SearchPlaceholder={Search}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtraWorkingDay;
